import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
} from '@mui/material';
import { EntityType, VersionScope } from '../../../types/snackableCourse';
import { useSnackableCourseAsync } from '../../../hooks/useSnackableCourse';
import { CourseGenerationActivity } from './CourseGenerationActivity';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  entityType: EntityType;
  entityId: number;
  entityName?: string;
  versionScope?: VersionScope | null;
}

const hasVersionScope = (versionScope?: VersionScope | null) => {
  return Boolean(
    versionScope?.product_id ||
    versionScope?.product_version_id ||
    versionScope?.product_license_id ||
    versionScope?.doc_version ||
    versionScope?.deployment_scope ||
    versionScope?.generation_scope
  );
};

const CourseDeploymentModal: React.FC<Props> = ({
                                                  open,
                                                  onClose,
                                                  onSuccess,
                                                  entityType,
                                                  entityId,
                                                  entityName,
                                                  versionScope = null,
                                                }) => {
  const [step, setStep] = useState(0);
  const [courseTitle, setCourseTitle] = useState('');
  const [courseDescription, setCourseDescription] = useState('');

  const {
    submitJob,
    jobId,
    jobStatus,
    polling,
    error: jobError,
  } = useSnackableCourseAsync();

  const scoped = hasVersionScope(versionScope);

  useEffect(() => {
    if (jobStatus) {
      if (jobStatus.status === 'completed') {
        setStep(2);
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
      } else if (jobStatus.status === 'failed') {
        setStep(3);
      }
    }
  }, [jobStatus, onSuccess]);

  const handleCreate = async () => {
    setStep(1);

    try {
      await submitJob({
        entity_type: entityType,
        entity_id: entityId,
        ...(versionScope || {}),
      });
    } catch (error) {
      console.error('Failed to submit course generation:', error);
      setStep(3);
    }
  };

  const handleClose = () => {
    if (!polling && step !== 1) {
      setStep(0);
      setCourseTitle('');
      setCourseDescription('');
      onClose();
    }
  };

  const steps = ['Configure', 'Generating', 'Complete'];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Snackable Course
        {entityName && (
          <Typography variant="caption" display="block" color="text.secondary">
            For {entityType}: {entityName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3, mt: 2 }}>
          <Stepper activeStep={step}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {step === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              This will create a new AI-generated snackable course for this {entityType}. Generation may take 2-5 minutes.
            </Alert>

            {scoped && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600}>
                  Version-scoped generation
                </Typography>
                <Typography variant="body2">
                  This course will be generated using only the selected product/version context where available.
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  {versionScope?.doc_version && <Chip size="small" label={`Version: ${versionScope.doc_version}`} />}
                  {versionScope?.product_version_id && <Chip size="small" label={`Version ID: ${versionScope.product_version_id}`} />}
                  {versionScope?.deployment_scope && <Chip size="small" label={`Deployment: ${versionScope.deployment_scope}`} />}
                  {versionScope?.product_license_id && <Chip size="small" label={`License ID: ${versionScope.product_license_id}`} />}
                </Box>
              </Alert>
            )}

            <TextField
              fullWidth
              label="Course Title (Optional)"
              value={courseTitle}
              onChange={(e) => setCourseTitle(e.target.value)}
              placeholder={`Course for ${entityName || entityType}`}
              sx={{ mb: 2 }}
              helperText="Leave blank to auto-generate from entity data"
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Course Description (Optional)"
              value={courseDescription}
              onChange={(e) => setCourseDescription(e.target.value)}
              placeholder={`AI-generated course content for ${entityName || entityType}`}
              helperText="Leave blank to auto-generate from entity data"
            />
          </Box>
        )}

        {step === 1 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              gap: 2,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6">
              {jobStatus?.status === 'running'
                ? 'Generating Course...'
                : 'Starting Generation...'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <CourseGenerationActivity active={polling} startedAt={jobStatus?.started_at} />
            </Typography>
            {jobId && (
              <Typography variant="caption" color="text.secondary">
                Job ID: {jobId}
              </Typography>
            )}
          </Box>
        )}

        {step === 2 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Course Created Successfully!</strong>
            </Typography>
            <Typography variant="body2">
              Your snackable course has been generated and is ready to manage.
            </Typography>
          </Alert>
        )}

        {step === 3 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Generation Failed</strong>
            </Typography>
            <Typography variant="body2">
              {jobStatus?.error_message ||
                jobError ||
                'Failed to generate course. Please try again.'}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {step === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={polling}
            >
              Generate Course
            </Button>
          </>
        )}

        {step === 1 && (
          <Button onClick={handleClose} disabled>
            Generating...
          </Button>
        )}

        {(step === 2 || step === 3) && (
          <Button variant="contained" onClick={handleClose}>
            {step === 2 ? 'Open Manager' : 'Close'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CourseDeploymentModal;